import React, { useState } from 'react';
import { DatePicker, Form, Tooltip } from 'antd';
import { useT } from 'hooks/Locale/useT';
import { constructValidationRules } from './validations';
import './styles.scss';
import type { RangePickerProps } from 'antd/es/date-picker';

type DateRangeProps = RangePickerProps & {
    name?: string;
    label?: string;
    validation?: Record<string, any>;
    customRules?: any[];
    customLayout?: { labelCol?: object; wrapperCol?: object };
    help?: string;
    visible?: boolean;
    className?: string;
    isFormItem?: boolean;
    hint?: string;
    /** The end may be at most this many days (inclusive) after the start. */
    maxDays?: number;
};

const { RangePicker } = DatePicker;

type RangeValue = RangePickerProps['value'];

/**
 * Range picker whose end is limited to `maxDays` after the start. Picks are kept in a local
 * draft and only a complete, valid range is handed to `onChange` (i.e. the form), so a stale
 * end can never leak into form state. rc-picker keeps the previous end after a new start and
 * opens the end panel next to it; dropping an end outside the window from the draft makes the
 * panel open at the start instead.
 */
const CappedRangePicker: React.FC<RangePickerProps & { maxDays: number }> = ({
    maxDays,
    value,
    onChange,
    disabledDate,
    onOpenChange,
    ...props
}) => {
    const [draft, setDraft] = useState<RangeValue>(null);
    // Which range input has focus while the picker is open.
    const [side, setSide] = useState<'start' | 'end' | null>(null);
    const shown = draft ?? value ?? null;

    const withinWindow = (range: RangeValue): boolean => {
        const [start, end] = range ?? [null, null];

        return !!start && !!end && !end.isBefore(start, 'day') && end.diff(start, 'days') < maxDays;
    };

    return (
        <RangePicker
            {...props}
            value={shown}
            disabledDate={(current) => {
                const start = shown?.[0];
                const tooLate = side === 'end' && !!start && current.diff(start, 'days') >= maxDays;

                return tooLate || !!disabledDate?.(current);
            }}
            onFocus={(e) => {
                const firstInput = e.target.closest('.ant-picker-range')?.querySelector('input');
                setSide(firstInput === e.target ? 'start' : 'end');
            }}
            onCalendarChange={(dates, _, info: { range: 'start' | 'end' }) => {
                setDraft(
                    info.range === 'start' && !withinWindow(dates)
                        ? [dates?.[0] ?? null, null]
                        : dates,
                );
            }}
            onChange={(dates, strings) => {
                // null = cleared via allowClear.
                if (dates === null || withinWindow(dates)) {
                    setDraft(null);
                    onChange?.(dates, strings);
                }
            }}
            onOpenChange={(open) => {
                if (!open) {
                    setDraft(null);
                    setSide(null);
                }
                onOpenChange?.(open);
            }}
        />
    );
};

const DateRange: React.FC<DateRangeProps> = ({
    name,
    label,
    validation = {},
    customRules,
    customLayout,
    help,
    visible = true,
    className,
    isFormItem = true,
    hint,
    maxDays,
    ...props
}) => {
    const t = useT();

    let labelCol: any = { span: 8 };
    let wrapperCol: any = { span: 6 };

    if (customLayout) {
        labelCol = customLayout.labelCol ?? undefined;
        wrapperCol = customLayout.wrapperCol ?? undefined;
    }

    const dateRangeComponent = maxDays ? (
        <CappedRangePicker {...props} maxDays={maxDays} className="default-date-range" />
    ) : (
        <RangePicker {...props} className="default-date-range" />
    );

    return isFormItem ? (
        <Form.Item
            className={className}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
            name={name}
            label={
                hint ? (
                    <span>
                        {t(label!)}
                        <Tooltip title={t(hint)}>
                            <i className="fal fa-question-circle header-item" />
                        </Tooltip>
                    </span>
                ) : (
                    t(label!)
                )
            }
            rules={customRules || constructValidationRules(validation, t)}
            help={help}
            style={{ display: visible ? '' : 'none' }}
        >
            {dateRangeComponent}
        </Form.Item>
    ) : (
        dateRangeComponent
    );
};

export default DateRange;
